import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { SESSION_EXPIRED_EVENT } from '../../utils/tokenRefreshInterceptor';
import { getDefaultRedirect } from '../../utils/navigation';
import { useProfileSheet, ProfileSheetView } from '../../contexts/ProfileSheetContext';
import { ContentType, SubType } from '../../api/types';
import LoginView from './sheet/views/LoginView';
import ForgotPasswordView from './sheet/views/ForgotPasswordView';
import RegisterView from './sheet/views/RegisterView';
import ProfileView from './sheet/views/ProfileView';
import CreatePasswordView from './sheet/views/CreatePasswordView';
import ResetPasswordView from './sheet/views/ResetPasswordView';
import ApiKeysView from './sheet/views/ApiKeysView';

interface FilterState {
  activeContentTypes: Set<ContentType>;
  activeSubTypes: Set<SubType>;
}

interface ProfileSheetProps {
  filterState: FilterState;
  updateContentTypes: (types: Set<ContentType>) => void;
  updateSubTypes: (types: Set<SubType>) => void;
}

/** Route segments that are app pages, not brand slugs. */
const RESERVED_SEGMENTS = new Set([
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'welcome',
  'accept-invitation',
  'change-password',
  'static',
  'admin',
  'assets',
  'publisher',
  'legacy',
]);

const showTriggerFor = (pathname: string, isAuthenticated: boolean): boolean => {
  if (pathname === '/') return isAuthenticated;
  if (pathname === '/all') return true;
  const segments = pathname.split('/').filter(Boolean);
  if (RESERVED_SEGMENTS.has(segments[0])) return false;
  // Brand pages only — deep links (articles, tend items) keep chrome
  // minimal. The brand trio's sibling pages count as brand pages.
  if (segments.length === 1) return true;
  return segments.length === 2 && (segments[1] === 'tended' || segments[1] === 'published');
};

const VIEW_HEADINGS: Record<ProfileSheetView, { eyebrow: string; title: string }> = {
  login: { eyebrow: 'ACCOUNT', title: 'Sign in' },
  forgot: { eyebrow: 'ACCOUNT', title: 'Reset password' },
  register: { eyebrow: 'ACCOUNT', title: 'Create account' },
  profile: { eyebrow: 'ACCOUNT', title: 'Profile' },
  'create-password': { eyebrow: 'WELCOME TO TEND', title: 'Create your password' },
  'reset-password': { eyebrow: 'ACCOUNT', title: 'Choose a new password' },
  'api-keys': { eyebrow: 'CONNECT', title: 'API keys' },
};

/**
 * The unified glass profile sheet: auth views (login / forgot / register),
 * token deep-link views (create / reset password), and the full profile.
 * Mounted once at App level, OUTSIDE the route Suspense (lazy-chunk loads
 * must never unmount it mid-animation).
 */
const ProfileSheet: React.FC<ProfileSheetProps> = ({
  filterState,
  updateContentTypes,
  updateSubTypes,
}) => {
  const { user, isAuthenticated } = useAuth();
  const { isOpen, view, closeSheet, toggleSheet, setView, openSheet } = useProfileSheet();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginNotice, setLoginNotice] = useState<string | null>(null);
  const [dismissedNoBrand, setDismissedNoBrand] = useState(false);
  // Where to send the user after a sign-in that came from a session-expiry
  // bounce (?signin=1&returnUrl=... on "/").
  const pendingReturnUrlRef = useRef<string | null>(null);

  const hasVerifiedBrand = !!user?.brands?.find((b) => b.verified && b.slug);
  const showTrigger = showTriggerFor(location.pathname, isAuthenticated);

  // First + last initial from the full name (same identity face as the
  // pinned footer, so the closed tile reads as the compressed profile).
  const initials = React.useMemo(() => {
    const name = (user?.full_name ?? '').trim();
    if (name) {
      const parts = name.split(/\s+/);
      const first = parts[0]?.[0] ?? '';
      const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
      return (first + last).toUpperCase();
    }
    return (user?.username?.[0] ?? '').toUpperCase();
  }, [user]);

  // Session-expiry landing: "/?signin=1&returnUrl=..." opens the sign-in
  // sheet (deferred past the provider's close-on-navigation effect) and
  // remembers where to return. The params are then cleaned off the URL.
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (searchParams.get('signin') !== '1') return;
    const returnUrl = searchParams.get('returnUrl');
    pendingReturnUrlRef.current = returnUrl;
    // Also stash in sessionStorage: RootRoute's render-phase Navigate to
    // the brand home can commit before our post-auth effect runs (closing
    // the sheet and dropping the ref) — the storage copy survives the race
    // and RootRoute itself consumes it first.
    if (returnUrl) {
      try { sessionStorage.setItem('tend:returnUrl', returnUrl); } catch { /* ignore */ }
    }
    setTimeout(() => {
      openSheet('login');
      navigate('/', { replace: true });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, searchParams]);

  // Session died while ON the homepage: no route guard fires there, so the
  // expiry broadcast is our cue to open the sign-in sheet directly (the old
  // interceptor bailed silently on '/', leaving a dead session behind a
  // working-looking page).
  useEffect(() => {
    const onExpired = () => {
      if (location.pathname === '/') openSheet('login');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [location.pathname, openSheet]);

  // Auth success (login/register completed while the sheet shows an auth
  // view): return to the pre-expiry page when one is pending; otherwise brand
  // users close the sheet and get forwarded to their brand — RootRoute does
  // that on "/", but the token deep-link routes (/welcome, /reset-password)
  // host the marketing page WITHOUT auto-forwarding, so navigate explicitly
  // there. Brandless users land on the profile view (NoBrandBlock explains).
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    // Token views included: create/reset-password auto-login on success.
    if (!['login', 'register', 'create-password', 'reset-password'].includes(view)) return;
    let returnUrl = pendingReturnUrlRef.current;
    pendingReturnUrlRef.current = null;
    // The sessionStorage copy survives the RootRoute race (see the signin
    // effect); consume whichever survived.
    try {
      returnUrl = returnUrl || sessionStorage.getItem('tend:returnUrl');
      sessionStorage.removeItem('tend:returnUrl');
    } catch { /* ignore */ }
    if (hasVerifiedBrand) {
      closeSheet();
      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      } else if (location.pathname === '/welcome' || location.pathname === '/reset-password') {
        navigate(getDefaultRedirect(user), { replace: true });
      }
    } else {
      setView('profile');
    }
  }, [isOpen, isAuthenticated, view, hasVerifiedBrand, closeSheet, setView, navigate, location.pathname, user]);

  // Brandless users on the marketing homepage get the sheet auto-opened once
  // per visit (replaces the old NoBrandNotice card).
  useEffect(() => {
    if (
      location.pathname === '/' &&
      isAuthenticated &&
      !hasVerifiedBrand &&
      !dismissedNoBrand &&
      !isOpen
    ) {
      openSheet('profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthenticated, hasVerifiedBrand]);

  // Closing on "/" while brandless counts as a dismissal for this visit.
  const handleClose = () => {
    if (location.pathname === '/' && isAuthenticated && !hasVerifiedBrand) {
      setDismissedNoBrand(true);
    }
    closeSheet();
  };

  // Escape closes.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location.pathname, isAuthenticated, hasVerifiedBrand]);

  // One-shot login notice (set by create/reset password success) clears when
  // the user moves off the login view.
  useEffect(() => {
    if (view !== 'login' && loginNotice) setLoginNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const heading = VIEW_HEADINGS[view];

  const renderView = () => {
    switch (view) {
      case 'login':
        return <LoginView notice={loginNotice} />;
      case 'forgot':
        return <ForgotPasswordView />;
      case 'register':
        return <RegisterView />;
      case 'create-password':
        return <CreatePasswordView onComplete={setLoginNotice} />;
      case 'reset-password':
        return <ResetPasswordView onComplete={setLoginNotice} />;
      case 'api-keys':
        return <ApiKeysView />;
      case 'profile':
      default:
        return (
          <ProfileView
            filterState={filterState}
            updateContentTypes={updateContentTypes}
            updateSubTypes={updateSubTypes}
          />
        );
    }
  };

  return (
    <>
      <div
        className={`tend-veil${isOpen ? ' is-on' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      <aside
        className={`tend-sheet tend-surface tend-f2${isOpen ? ' is-open' : ''}`}
        data-view={view}
        role="dialog"
        aria-modal="false"
        aria-label={heading.title}
      >
        {view === 'profile' ? (
          /* Profile anchors at the BOTTOM (identity + controls pinned in the
             sheet foot, rendered by ProfileView); up top only a slim close
             strip — no title header. */
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 14px 0', flexShrink: 0 }}>
            <button
              type="button"
              className="tend-btn tend-btn--icon tend-btn--icon-sm"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="tend-sheet-head" style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div className="tend-micro">{heading.eyebrow}</div>
              <div
                style={{
                  fontFamily: "'Inter Tight', sans-serif",
                  fontWeight: 700,
                  fontSize: 17,
                  color: 'var(--text)',
                  marginTop: 4,
                }}
              >
                {heading.title}
              </div>
            </div>
            <button
              type="button"
              className="tend-btn tend-btn--icon tend-btn--icon-sm"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        )}

        {view === 'profile' ? (
          /* ProfileView renders its own scroll body + pinned foot. */
          <div
            key="profile"
            className="tend-view-enter"
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          >
            {renderView()}
          </div>
        ) : (
          <div className="tend-sheet-body tend-scroll">
            <div key={view} className="tend-view-enter">
              {renderView()}
            </div>
          </div>
        )}
      </aside>

      {showTrigger && (
        <button
          type="button"
          className={`tend-trigger${isOpen ? ' is-hidden' : ''}`}
          onClick={toggleSheet}
          aria-label={isAuthenticated ? 'Open profile' : 'Sign in'}
        >
          <span className="tend-trigger-tile">
            {isAuthenticated && initials ? (
              initials
            ) : (
              <img
                src="https://nulewis-assets.s3.amazonaws.com/images/nu-logo.png"
                alt=""
              />
            )}
          </span>
        </button>
      )}
    </>
  );
};

export default ProfileSheet;
